import { useCallback, useEffect, useState } from "react";
import { Box, Flex, HStack, Icon, Text, VStack, useColorModeValue } from "@chakra-ui/react";
import { CheckCircle, Paperclip, PlayCircle, RotateCcw, Send, type LucideIcon } from "lucide-react";

import { settings } from "../../../../shared/settings";
import { useApiRequest } from "../../../../hooks/useApiRequest";
import { formatSp } from "../../../../lib/datetime";
import { OnflyLoading } from "../../../../tools/OnflyLoading";

export interface CandidateActivityLogEntry {
  id: number;
  action: string;
  mission_title: string | null;
  actor_email: string | null;
  actor_name: string | null;
  actor_role: string | null;
  created_at: string;
}

const ACTION_LABEL: Record<string, string> = {
  mission_reset: "reiniciou a missão",
  mission_approved: "aprovou a missão",
  mission_started: "iniciou a missão",
  mission_submitted: "enviou a missão",
  file_uploaded: "enviou um arquivo na missão",
};

const ACTION_ICON: Record<string, LucideIcon> = {
  mission_reset: RotateCcw,
  mission_approved: CheckCircle,
  mission_started: PlayCircle,
  mission_submitted: Send,
  file_uploaded: Paperclip,
};

function describeEntry(entry: CandidateActivityLogEntry): string {
  const verb = ACTION_LABEL[entry.action] ?? entry.action;
  return entry.mission_title ? `${verb} "${entry.mission_title}"` : verb;
}

export interface CandidateActivityLogSectionProps {
  email: string;
  refreshKey?: number;
}

export const CandidateActivityLogSection = ({ email, refreshKey }: CandidateActivityLogSectionProps) => {
  const { request } = useApiRequest();
  const [entries, setEntries] = useState<CandidateActivityLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const heading    = useColorModeValue("gray.800", "gray.100");
  const muted      = useColorModeValue("gray.500", "gray.400");
  const cardBorder = useColorModeValue("gray.100", "whiteAlpha.200");
  const iconBg     = useColorModeValue("blue.50", "rgba(0,158,251,0.12)");
  const iconColor  = useColorModeValue("blue.500", "blue.300");
  const emptyBg    = useColorModeValue("gray.100", "whiteAlpha.100");
  const emptyColor = useColorModeValue("gray.400", "gray.500");

  const loadEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await request({
        url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/candidates/${encodeURIComponent(email)}/activity-log`,
        params: { limit: 30 },
        method: "GET", skipToast: true, silent: true,
      });
      setEntries(data ?? []);
    } catch {
      setEntries([]);
    } finally {
      setIsLoading(false);
    }
  }, [email, request]);

  useEffect(() => { void loadEntries(); }, [loadEntries, refreshKey]);

  return (
    <Box>
      {isLoading ? (
        <Flex justify="center" py={6}><OnflyLoading containerSize={50} /></Flex>
      ) : entries.length === 0 ? (
        <Flex align="center" justify="center" py={6}>
          <Text fontSize="sm" color={emptyColor}>Nenhuma ação registrada ainda.</Text>
        </Flex>
      ) : (
        <VStack align="stretch" spacing={2}>
          {entries.map((entry) => {
            const ActionIcon = ACTION_ICON[entry.action] ?? RotateCcw;
            const actor = entry.actor_name || entry.actor_email || "Candidato";
            return (
              <Flex
                key={entry.id} align="center" gap={3}
                borderWidth="1px" borderColor={cardBorder} borderRadius="md" p={3}
              >
                <Flex
                  w="32px" h="32px" borderRadius="full" flexShrink={0}
                  bg={iconBg} color={iconColor} align="center" justify="center"
                >
                  <Icon as={ActionIcon} boxSize={4} />
                </Flex>
                <Box flex={1} minW={0}>
                  <Text fontSize="sm" noOfLines={2}>
                    <Text as="span" fontWeight="semibold" color={heading}>{actor}</Text>
                    {entry.actor_role && (
                      <Text as="span" color={muted}> ({entry.actor_role})</Text>
                    )}
                    {" "}
                    <Text as="span" color={muted}>{describeEntry(entry)}</Text>
                  </Text>
                </Box>
                <HStack flexShrink={0} spacing={1}>
                  <Text fontSize="xs" color={muted} whiteSpace="nowrap">
                    {formatSp(entry.created_at, "dd/MM/yyyy HH:mm")}
                  </Text>
                </HStack>
              </Flex>
            );
          })}
        </VStack>
      )}
    </Box>
  );
};
